/**
 * Desempeño de un recurso en un periodo: sus jornadas y lo que registró en ellas.
 *
 * Va aparte de la pantalla porque son las cuentas con las que se va a evaluar a una persona, y
 * esas cuentas tienen que poder probarse una por una. El usuario lo dijo así: *"esto va de la
 * mano con su performance de impulso de venta, entonces es medible"*.
 */

export interface JornadaDesempeno {
  /** Fecha de la jornada, YYYY-MM-DD. */
  fecha: string;
  pdvId: string | null;
  /** ¿Marcó ingreso ese día? */
  asistio: boolean;
  /** Canjes registrados por esta persona ese día. */
  canjes: number;
  /** ¿Corría alguna dinámica en esa tienda ese día? Decide si el 0 cuenta o no aplica. */
  conDinamica: boolean;
}

export interface Desempeno {
  jornadas: number;
  asistidas: number;
  /** Jornadas donde SÍ corría una dinámica: el denominador honesto del promedio. */
  jornadasConDinamica: number;
  canjes: number;
  /**
   * Canjes por jornada CON DINÁMICA a la que asistió. `null` si no hubo ninguna.
   *
   * El denominador es lo que hace justo o injusto este número. Dividir entre todas las jornadas
   * castigaría a quien trabajó semanas en tiendas sin promoción —donde no hay nada que
   * registrar— y haría parecer flojo a quien no tuvo oportunidad. Se cuentan solo las jornadas
   * donde de verdad había una dinámica corriendo y la persona estuvo.
   */
  promedio: number | null;
  /** Días con dinámica en los que asistió y no registró NADA. Es lo que hay que mirar. */
  enCero: number;
}

/** Una fila del resumen del equipo: la persona y su desempeño en el periodo. */
export interface FilaEquipo extends Desempeno {
  workerId: string;
  nombre: string;
  dni: string;
}

/**
 * Resume el periodo de TODO un equipo, una fila por persona (U9).
 *
 * ORDEN: primero quien tiene más días con dinámica **en cero**, después quien tiene peor promedio.
 * No se ordena alfabéticamente ni por el mejor: esta pantalla existe para encontrar a quién hay
 * que ayudar, y lo que hay que mirar tiene que estar arriba sin buscarlo. Quien no tuvo ninguna
 * jornada con dinámica —promedio `null`— va al final: no es que le haya ido mal, es que no tuvo
 * dónde registrar, y mezclarlo con los de promedio bajo sería acusarlo de algo que no pasó.
 */
export function resumirEquipo(
  jornadas: (JornadaDesempeno & { workerId: string; nombre: string; dni: string })[],
): FilaEquipo[] {
  const porPersona = new Map<string, typeof jornadas>();
  for (const j of jornadas) {
    const lista = porPersona.get(j.workerId) ?? [];
    lista.push(j);
    porPersona.set(j.workerId, lista);
  }

  return [...porPersona.values()]
    .map((lista) => ({
      workerId: lista[0].workerId,
      nombre: lista[0].nombre,
      dni: lista[0].dni,
      ...resumirDesempeno(lista),
    }))
    .sort((a, b) => {
      if (b.enCero !== a.enCero) return b.enCero - a.enCero;
      // `null` (sin jornadas con dinámica) siempre al final, sin importar el resto.
      if (a.promedio === null && b.promedio === null) return a.nombre.localeCompare(b.nombre);
      if (a.promedio === null) return 1;
      if (b.promedio === null) return -1;
      if (a.promedio !== b.promedio) return a.promedio - b.promedio;
      return a.nombre.localeCompare(b.nombre);
    });
}

/** Totales del equipo, para la línea de arriba. */
export function totalesEquipo(filas: FilaEquipo[]): Desempeno & { personas: number } {
  const conDinamica = filas.reduce((s, f) => s + f.jornadasConDinamica, 0);
  const canjesConDinamica = filas.reduce((s, f) => s + (f.promedio ?? 0) * f.jornadasConDinamica, 0);
  return {
    personas: filas.length,
    jornadas: filas.reduce((s, f) => s + f.jornadas, 0),
    asistidas: filas.reduce((s, f) => s + f.asistidas, 0),
    jornadasConDinamica: conDinamica,
    canjes: filas.reduce((s, f) => s + f.canjes, 0),
    // El promedio del equipo se calcula sobre el total, NO promediando los promedios: promediar
    // promedios le da el mismo peso a quien trabajó 20 días que a quien trabajó 1.
    promedio: conDinamica === 0 ? null : Math.round((canjesConDinamica / conDinamica) * 10) / 10,
    enCero: filas.reduce((s, f) => s + f.enCero, 0),
  };
}

export function resumirDesempeno(jornadas: JornadaDesempeno[]): Desempeno {
  const asistidas = jornadas.filter((j) => j.asistio);
  const conDinamica = asistidas.filter((j) => j.conDinamica);
  const canjes = jornadas.reduce((s, j) => s + j.canjes, 0);
  return {
    jornadas: jornadas.length,
    asistidas: asistidas.length,
    jornadasConDinamica: conDinamica.length,
    canjes,
    promedio: conDinamica.length === 0
      ? null
      : Math.round((conDinamica.reduce((s, j) => s + j.canjes, 0) / conDinamica.length) * 10) / 10,
    enCero: conDinamica.filter((j) => j.canjes === 0).length,
  };
}
